import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  DocumentChartBarIcon,
  UsersIcon,
  UserIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  EyeIcon,
  PlusIcon,
  FunnelIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { H1, H2, H3, Body, Card, Badge, Button, Spinner } from '../ui';
import { Select } from '../forms';
import { StatisticsCard } from '../ui/StatisticsCard';
import { useChildren, useClassrooms, useLearningAreas, useReports, useDownloadReport, useRegenerateReport } from '../../hooks/api';
import { formatDate } from '../../utils/date';

interface ReportsDashboardProps {
  onCreateReport: (type: string, config?: any) => void;
  onViewReport: (reportId: string) => void;
  canCreateReports: boolean;
  canViewReports: boolean;
}

const ReportsDashboard: React.FC<ReportsDashboardProps> = ({
  onCreateReport,
  onViewReport,
  canCreateReports,
  canViewReports,
}) => {
  const [selectedReportType, setSelectedReportType] = useState<string>('');
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'term' | 'year'>('term');
  
  const { data: childrenData } = useChildren();
  const { data: classroomsData } = useClassrooms();
  const { data: learningAreas } = useLearningAreas();
  const { data: recentReports = [], isLoading: reportsLoading } = useReports({ limit: 10 });
  const downloadReport = useDownloadReport();
  const regenerateReport = useRegenerateReport();

  // Heuristic: if a report has been "pending" or "generating" for more
  // than 30s, it's almost certainly stuck (the worker is single-threaded
  // and a real run finishes well within that). We show a Regenerate
  // button so the user has a recovery path instead of staring at a
  // spinner forever -- this happens with seed-data rows that were
  // inserted directly into the DB without enqueuing a background task.
  const STUCK_THRESHOLD_MS = 30_000;
  const isStuck = (report: { status: string; created_date: string }) => {
    if (report.status !== 'pending' && report.status !== 'generating') return false;
    const ageMs = Date.now() - new Date(report.created_date).getTime();
    return ageMs > STUCK_THRESHOLD_MS;
  };

  const children = childrenData?.items || [];
  const classrooms = classroomsData || [];

  const reportTypeLabels: Record<string, string> = {
    student_progress: 'Individual Report',
    classroom_report: 'Class Report',
    assessment_overview: 'Assessment Matrix',
    observation_summary: 'Analytics Report',
    learning_area_summary: 'Learning Area Summary',
    term_report: 'Term Report',
  };

  const reportTypes = [
    {
      id: 'individual',
      title: 'Individual Child Report',
      description: 'Comprehensive progress report for a single child',
      icon: UserIcon,
      color: 'bg-blue-500',
      features: ['Assessment progress', 'Observation summaries', 'Learning outcomes', 'Recommendations'],
    },
    {
      id: 'classroom',
      title: 'Classroom Report',
      description: 'Overview of all children in a classroom',
      icon: UsersIcon,
      color: 'bg-green-500',
      features: ['Class performance', 'Individual summaries', 'Curriculum coverage', 'Trends analysis'],
    },
    {
      id: 'assessment-matrix',
      title: 'Assessment Matrix',
      description: 'Grid view of assessments across learning areas',
      icon: DocumentChartBarIcon,
      color: 'bg-purple-500',
      features: ['Learning area coverage', 'Skill progression', 'Gap analysis', 'Visual matrix'],
    },
    {
      id: 'analytics',
      title: 'Analytics Report',
      description: 'Data-driven insights and trends',
      icon: ChartBarIcon,
      color: 'bg-orange-500',
      features: ['Performance trends', 'Comparative analysis', 'Predictive insights', 'Data visualization'],
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'generating': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return DocumentTextIcon;
      case 'generating': return ClockIcon;
      default: return DocumentTextIcon;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <H1 className="mb-2">Reports & Analytics</H1>
          <Body className="text-gray-600 dark:text-gray-400">
            Generate comprehensive reports and analyze child progress data.
          </Body>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            options={[
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'term', label: 'This Term' },
              { value: 'year', label: 'This Year' },
            ]}
          />
          {canCreateReports && (
            <Button variant="primary" onClick={() => onCreateReport('wizard')}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Report
            </Button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatisticsCard
          title="Total Reports"
          value={recentReports.length}
          icon={DocumentTextIcon}
          color="primary"
        />
        <StatisticsCard
          title="Children Covered"
          value={children.length}
          icon={UserIcon}
          color="success"
        />
        <StatisticsCard
          title="Classrooms"
          value={classrooms.length}
          icon={UsersIcon}
          color="info"
        />
        <StatisticsCard
          title="Learning Areas"
          value={learningAreas?.length || 0}
          icon={AcademicCapIcon}
          color="secondary"
        />
      </div>

      {/* Report Types Grid */}
      <div>
        <H2 className="mb-4">Report Types</H2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reportTypes.map((reportType) => (
            <motion.div
              key={reportType.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <div 
                  className="p-6"
                  onClick={() => canCreateReports && onCreateReport(reportType.id)}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg ${reportType.color} text-white`}>
                      <reportType.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <H3 className="mb-2">{reportType.title}</H3>
                      <Body className="text-gray-600 dark:text-gray-400 mb-3">
                        {reportType.description}
                      </Body>
                      <div className="space-y-1">
                        {reportType.features.map((feature, index) => (
                          <div key={index} className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                            <div className="w-1 h-1 bg-gray-400 rounded-full" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {canCreateReports && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCreateReport(reportType.id);
                        }}
                      >
                        Generate Report
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Reports */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <H2>Recent Reports</H2>
          <Button variant="ghost" size="sm">
            <FunnelIcon className="w-4 h-4 mr-2" />
            Filter
          </Button>
        </div>
        
        {reportsLoading ? (
          <Card>
            <div className="p-8 text-center">
              <Spinner size="lg" />
              <Body className="text-gray-500 mt-4">Loading reports...</Body>
            </div>
          </Card>
        ) : recentReports.length === 0 ? (
          <Card>
            <div className="p-8 text-center">
              <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <H3 className="text-gray-500 mb-2">No Reports Generated</H3>
              <Body className="text-gray-400 mb-4">
                Create your first report to get started with analytics.
              </Body>
              {canCreateReports && (
                <Button variant="primary" onClick={() => onCreateReport('wizard')}>
                  Create First Report
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {recentReports.map((report) => {
              const StatusIcon = getStatusIcon(report.status);
              return (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <div className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center space-x-4 min-w-0">
                          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0">
                            <StatusIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                          </div>
                          <div className="min-w-0">
                            <H3 className="mb-1 truncate">{report.title || reportTypeLabels[report.report_type] || report.report_type}</H3>
                            <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-sm text-gray-500 dark:text-gray-400">
                              <span>{reportTypeLabels[report.report_type] || report.report_type}</span>
                              <span className="hidden sm:inline">&#183;</span>
                              <span>{formatDate(report.created_date)}</span>
                              {report.report_format && (
                                <>
                                  <span className="hidden sm:inline">&#183;</span>
                                  <span>{report.report_format.toUpperCase()}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                          <Badge
                            variant={getStatusColor(report.status)}
                            size="sm"
                          >
                            {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                          </Badge>

                          {canViewReports && report.status === 'completed' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onViewReport(report.id)}
                              >
                                <EyeIcon className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  downloadReport.mutate({
                                    id: report.id,
                                    filename: `${report.title || 'report'}.${report.report_format || 'pdf'}`,
                                  });
                                }}
                              >
                                <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                            </>
                          )}
                          {(report.status === 'generating' || report.status === 'pending') && !isStuck(report) && (
                            <div className="flex items-center space-x-2">
                              <Spinner size="sm" />
                              <span className="text-sm text-gray-500 dark:text-gray-400">Generating...</span>
                            </div>
                          )}

                          {((report.status === 'pending' || report.status === 'generating') && isStuck(report)) && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => regenerateReport.mutate(report.id)}
                              disabled={regenerateReport.isPending}
                              title="This report has been pending for a while. Click to retry generation."
                            >
                              <ArrowPathIcon className="w-4 h-4 mr-1" />
                              Retry
                            </Button>
                          )}

                          {report.status === 'failed' && canCreateReports && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => regenerateReport.mutate(report.id)}
                              disabled={regenerateReport.isPending}
                            >
                              <ArrowPathIcon className="w-4 h-4 mr-1" />
                              Retry
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <Card>
        <div className="p-6">
          <H2 className="mb-4">Quick Actions</H2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-blue-100 dark:border-blue-800 hover:border-blue-200 dark:hover:border-blue-700 transition-colors cursor-pointer"
              onClick={() => canCreateReports && onCreateReport('individual')}
            >
              <div className="flex items-center space-x-3">
                <UserIcon className="w-8 h-8 text-blue-600" />
                <div>
                  <H3 className="text-blue-900 dark:text-blue-200">Individual Report</H3>
                  <Body className="text-sm text-blue-700 dark:text-blue-300">
                    Generate report for a specific child
                  </Body>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-100 dark:border-green-800 hover:border-green-200 dark:hover:border-green-700 transition-colors cursor-pointer"
              onClick={() => canCreateReports && onCreateReport('classroom')}
            >
              <div className="flex items-center space-x-3">
                <UsersIcon className="w-8 h-8 text-green-600" />
                <div>
                  <H3 className="text-green-900 dark:text-green-200">Classroom Report</H3>
                  <Body className="text-sm text-green-700 dark:text-green-300">
                    Overview of classroom performance
                  </Body>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-2 border-purple-100 dark:border-purple-800 hover:border-purple-200 dark:hover:border-purple-700 transition-colors cursor-pointer"
              onClick={() => canCreateReports && onCreateReport('analytics')}
            >
              <div className="flex items-center space-x-3">
                <ChartBarIcon className="w-8 h-8 text-purple-600" />
                <div>
                  <H3 className="text-purple-900 dark:text-purple-200">Analytics Dashboard</H3>
                  <Body className="text-sm text-purple-700 dark:text-purple-300">
                    Data insights and trends
                  </Body>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ReportsDashboard;
